/**
 * The floor renderer: imperative Three.js on WebGPU, and the only code allowed
 * on the per-frame path (docs/13-architecture.md). React is not imported here
 * and never will be.
 *
 * What it does each frame is deliberately the whole architecture in miniature:
 * take the two most recent snapshots, interpolate between them by wall-clock
 * alpha, and write the result straight into one instanced mesh's transform
 * array. Interpolation is *presentation* — nothing computed here is ever sent
 * back — which is what lets a 10 Hz sim look continuous without touching the
 * determinism law.
 *
 * Since B0.2 there is a second instanced mesh for the built site: rooms as
 * floor plates, tools as boxes, both scaled per instance. It is rebuilt only
 * when the snapshot's `structureEpoch` moves — the layout is in every publish,
 * but reading it every frame would be work nobody asked for.
 *
 * **The colour rule, stated once.** What a thing *is* — a room type, a tool
 * type, a trade — is authored content and comes from the catalog, which comes
 * from Rust: an identity palette in the renderer would be art hidden in code.
 * What a thing is *doing* — a lot queued, an agent on a break, a cursor over an
 * illegal cell — is interface signalling, and the tables for that live here,
 * because they are how the screen speaks rather than what the world contains.
 *
 * B0.4 moved the crowd onto the GPU, which is the piece of this the roadmap
 * always meant to prove. The two most recent published agent arrays are uploaded
 * verbatim into storage buffers and a **compute pass** does the rest:
 * interpolation, the tint for what each body is doing, a bob for the walking
 * ones and a stoop for the tired ones. There is no per-entity JavaScript on the
 * frame path at all now — the main thread's whole contribution to a crowd of any
 * size is two `set()` calls and one dispatch, which is the property B0.6 will
 * put under stress.
 *
 * The lots stayed on the CPU deliberately: there are at most a WIP cap's worth
 * of them, and a second compute pipeline to maintain would be scaffolding built
 * for sixty-four boxes.
 *
 * **B0.6 added the LOD tiers and then measured them into a footnote.** Two radii
 * around whatever the camera is looking at decide who bobs, who stoops and who
 * is drawn at all — four scalars a frame, evaluated in the compute pass, with no
 * per-body work on the main thread (see {@link Lod}). Measured against `?lod=off`
 * on an RX 7900 XTX they bought *nothing outside sampling noise*, because at the
 * default camera the whole plot is on screen and the GPU was never the
 * constraint. They stay because they are nearly free and a later era's floor is
 * denser; the thing to remember is that **LOD is not what keeps this frame
 * inside budget** (`web/platform-matrix.md`).
 */

import * as THREE from 'three/webgpu';
import {
  Fn,
  float,
  instanceIndex,
  instancedArray,
  mix,
  sin,
  positionLocal,
  texture as textureNode,
  uniform,
  uv,
  vec3,
} from 'three/tsl';
import type { SiteCatalogUi } from '../bridge';

/** One published state, as the renderer needs it. */
export interface FrameSnapshot {
  seq: number;
  count: number;
  instances: Float32Array;
  structures: Float32Array;
  structureCount: number;
  structureEpoch: number;
  /** Lots in flight: `[x, z, state, progress]` each. */
  lots: Float32Array;
  lotCount: number;
  /** The floor plane, one byte per plot cell, row-major from the south-west. */
  heat: Uint8Array;
  heatW: number;
  heatH: number;
  /** Which field that plane carries: `0` WIP, `1` utilization, `2` waiting. */
  heatView: number;
  plotW: number;
  plotH: number;
}

/** The build cursor's ghost, in plot cells. */
export interface CursorGhost {
  /** South-west corner. */
  x: number;
  y: number;
  w: number;
  h: number;
  height: number;
  /** What the sim said when asked: legal, refused, or not yet answered. */
  verdict: 'legal' | 'refused' | 'unknown';
}

/** What `render` reports back for the HUD's frame budget. */
export interface FrameCost {
  /** Time spent reading the boundary and building transforms, in ms. */
  boundaryMs: number;
  /** Time spent in `renderer.render`, in ms. */
  renderMs: number;
  /** Instances drawn. */
  drawn: number;
  /** Structures drawn. */
  structures: number;
  /** Lots drawn. */
  lots: number;
  /** Interpolation alpha used, for eyeballing publish jitter. */
  alpha: number;
  /** The LOD tiers in force this frame, in world units. See {@link Lod}. */
  lod: Lod;
}

/**
 * **The crowd LOD stub** (B0.6): two radii around whatever the camera is looking
 * at, in world units — which are metres, because one grid cell is one metre
 * (docs/06-production.md).
 *
 * Bodies inside `detailRadius` bob when they walk and stoop when they are tired.
 * Bodies outside it stand still and straight, which costs the shader nothing and
 * is invisible at that distance. Bodies outside `cullRadius` are scaled to
 * nothing.
 *
 * It is a **stub and says so**: the radii are distances from the view centre
 * rather than a frustum test, the culled instances still run a vertex shader
 * because compacting the buffer would mean a second compute pass, and there is
 * only one mesh so there is no mesh swap. What it is far enough to do is the one
 * thing B0.6 needs — let the bench run the same load with the tiers on and off
 * and price them (`web/scripts/bench.mjs`, `?lod=off`).
 *
 * What it deliberately does **not** do is count anything per body. Knowing how
 * many bodies fell in each tier would mean walking the instance array on the
 * main thread every frame, which is exactly the per-entity loop B0.4 removed.
 * The measurement is the frame-time difference, not a tally.
 */
export interface Lod {
  enabled: boolean;
  detailRadius: number;
  cullRadius: number;
}

/** LOD off: radii past any plot this build can author. */
const LOD_OFF: Lod = { enabled: false, detailRadius: 1e6, cullRadius: 1e6 };

/**
 * How far past the visible half-extent a body is still drawn, and what share of
 * that distance still earns full detail. Both by eye, both cheap to move: the
 * cull margin is generous because an orthographic camera on the isometric
 * diagonal sees further along one axis than a radius describes.
 */
const CULL_MARGIN = 1.35;
const DETAIL_SHARE = 0.45;

/** Structure classes, as published. */
const CLASS_TOOL = 1;
/** Structure flags, as published. */
const FLAG_SELECTED = 1;

/**
 * What an agent is doing, as the sim codes it: `role × 8 + doing`. The compute
 * pass looks the whole code up in one table, so both halves are read without
 * arithmetic in the shader.
 */
const DOING_SLOTS = 8;

/**
 * How each state recolours the body wearing it: an accent, and how far to pull
 * the role's own colour toward it.
 *
 * The *role* colour is authored content and comes from the catalog, like every
 * room and tool colour since B0.2 — a technician should be recognisable as a
 * technician. What lives here is **state signalling**, which is interface
 * rather than art: the same standing the lot-state tints and the cursor ghost
 * have. Idle is pulled toward grey and the two off-the-floor states are pulled
 * hard toward their own colour, because "who is not working" is the thing a
 * glance at the floor is supposed to answer.
 */
const DOING_ACCENTS: { accent: number; weight: number }[] = [
  { accent: 0x39424b, weight: 0.65 }, // idle — sunk into the background
  { accent: 0xffffff, weight: 0.1 }, // walking
  { accent: 0xe0b070, weight: 0.55 }, // carrying a lot
  { accent: 0xb9e08f, weight: 0.8 }, // gowning — the airlock's own green
  { accent: 0x7fd0a8, weight: 0.5 }, // loading a tool
  { accent: 0x9b8fd0, weight: 0.6 }, // repairing
  { accent: 0xc8a27a, weight: 0.8 }, // on a break
  { accent: 0x2c333c, weight: 0.9 }, // off shift (M1.3) — the lights are out
];

/**
 * Lot tints by state — waiting, carried, queued, running, stuck — with an
 * expedited lot carrying a half-step on top. A queue should read as a colour
 * and not only as a cluster.
 */
const LOT_COLORS = [0xd8d2c0, 0xf0e0a0, 0xe0b070, 0x7fd0a8, 0xe06a6a];
/** What an expedited lot is painted instead. */
const HOT_COLOR = 0xff4fa0;

const GHOST_COLORS = { legal: 0x8fd0a0, refused: 0xe08a8a, unknown: 0xa8b4c0 };

/**
 * How the floor plane is painted, by the field the sim says it is carrying
 * (B0.5). Three fields through one set of bytes, and three colours so a glance
 * at the floor cannot mistake one for another — amber for where the work is,
 * cyan for how hard the machines are working, red for where nobody has come.
 *
 * Interface signalling, so it lives here rather than in the catalog: this is how
 * the screen speaks about a measurement, not what the world contains.
 */
const HEAT_TINTS = [0xff9e47, 0x59c8d8, 0xe0574f];

/**
 * How the floor plane's byte becomes ink (B0.7). Below 1 it lifts the quiet end
 * of the range, which is where a queue-length tell lives: the difference between
 * one lot and three is the diagnosis, and under a linear ramp both were a faint
 * smudge. See `rebuildHeat` for why this is presentation rather than a change to
 * what the sim measures.
 */
const HEAT_GAMMA = 0.6;

/** How far the camera closes in when an overlay asks it to fly to something. */
const FOCUS_ZOOM = 0.42;
/** Share of the remaining distance the camera covers each frame. */
const FLY_RATE = 0.12;

export class FloorRenderer {
  readonly renderer: THREE.WebGPURenderer;
  private readonly scene = new THREE.Scene();
  private readonly camera: THREE.OrthographicCamera;

  /** The crowd: one instanced draw, advanced and posed by a compute pass. */
  private readonly crowd: ReturnType<typeof buildCrowd>;
  private readonly capacity: number;

  private readonly structures: THREE.InstancedMesh;
  private readonly structureMatrices: Float32Array;
  /** The structure boxes as last uploaded, for `pickAt`'s ray test. */
  private readonly pickBoxes: { x: number; z: number; w: number; d: number; h: number }[] = [];
  private readonly structureColors: Float32Array;
  private readonly structureCapacity: number;
  /** Last epoch uploaded, so a still layout costs nothing per frame. */
  private structureEpoch = -1;

  private readonly lots: THREE.InstancedMesh;
  private readonly lotMatrices: Float32Array;
  private readonly lotColors: Float32Array;
  private readonly lotCapacity: number;

  private readonly ghost: THREE.Mesh;
  private readonly ghostMaterial: THREE.MeshBasicNodeMaterial;

  /** The floor plane: a one-byte-per-cell texture over a quad on the ground. */
  private heatTexture: THREE.DataTexture | null = null;
  private heatPlane: THREE.Mesh | null = null;
  private heatMaterial: THREE.MeshBasicNodeMaterial | null = null;
  /** The field the plane is currently painted for. */
  private heatView = -1;

  /**
   * Where the camera is looking and how close, and where it is *going*. The
   * overlay's "fly to the queue" (docs/11-interface.md — every number pointable)
   * writes the target; the frame loop walks toward it. Presentation only: the
   * camera is not sim state and nothing here is ever sent back.
   */
  private readonly view = { x: 0, z: 0, zoom: 1 };
  private readonly viewTarget = { x: 0, z: 0, zoom: 1 };

  /** Ground, rebuilt when the plot size changes (a new site, a new era). */
  private ground: THREE.Group | null = null;
  private plotW = 0;
  private plotH = 0;

  /** Palettes taken from the catalog, indexed by type. */
  private readonly roomColors: THREE.Color[];
  private readonly toolColors: THREE.Color[];
  /** Reused per frame so a colour write is not an allocation. */
  private readonly scratch = new THREE.Color();

  /**
   * Whether the LOD tiers are in force. Off is not a fallback — it is the
   * control case the bench measures against, and the only way to price what the
   * tiers buy on a given machine.
   */
  private lodEnabled = true;

  constructor(
    canvas: HTMLCanvasElement,
    capacity: number,
    structureCapacity: number,
    lotCapacity: number,
    catalog: SiteCatalogUi,
  ) {
    this.capacity = capacity;
    this.structureCapacity = structureCapacity;
    this.lotCapacity = lotCapacity;
    this.roomColors = catalog.rooms.map((room) => new THREE.Color(room.color));
    this.toolColors = catalog.tools.map((tool) => new THREE.Color(tool.color));

    this.renderer = new THREE.WebGPURenderer({ canvas, antialias: true });
    this.renderer.setClearColor(0x101418, 1);

    // Isometric: an orthographic camera on the 45°/35.26° diagonal, which is
    // the projection docs/11-interface.md's live-in-the-building view assumes.
    this.camera = new THREE.OrthographicCamera(-40, 40, 40, -40, -500, 1000);
    this.camera.position.set(60, 60, 60);
    this.camera.lookAt(0, 0, 0);

    this.scene.add(new THREE.AmbientLight(0xffffff, 1.4));
    const key = new THREE.DirectionalLight(0xffffff, 2.2);
    key.position.set(40, 80, 25);
    this.scene.add(key);

    this.structures = new THREE.InstancedMesh(
      new THREE.BoxGeometry(1, 1, 1),
      new THREE.MeshStandardNodeMaterial({ roughness: 0.85, metalness: 0.02 }),
      structureCapacity,
    );
    this.structures.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.structures.instanceColor = new THREE.InstancedBufferAttribute(
      new Float32Array(structureCapacity * 3),
      3,
    );
    this.structures.instanceColor.setUsage(THREE.DynamicDrawUsage);
    this.structures.frustumCulled = false;
    this.structures.count = 0;
    this.scene.add(this.structures);
    this.structureMatrices = this.structures.instanceMatrix.array as Float32Array;
    this.structureColors = this.structures.instanceColor.array as Float32Array;

    this.crowd = buildCrowd(capacity, catalog);
    this.scene.add(this.crowd.mesh);

    // Lots: small crates, above the floor and below head height, so a queue is
    // legible from the isometric camera without hiding the tool it is on.
    this.lots = new THREE.InstancedMesh(
      new THREE.BoxGeometry(0.9, 0.6, 0.9),
      new THREE.MeshStandardNodeMaterial({ roughness: 0.5, metalness: 0.1 }),
      lotCapacity,
    );
    this.lots.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.lots.instanceColor = new THREE.InstancedBufferAttribute(
      new Float32Array(lotCapacity * 3),
      3,
    );
    this.lots.instanceColor.setUsage(THREE.DynamicDrawUsage);
    this.lots.frustumCulled = false;
    this.lots.count = 0;
    this.scene.add(this.lots);
    this.lotMatrices = this.lots.instanceMatrix.array as Float32Array;
    this.lotColors = this.lots.instanceColor.array as Float32Array;
    for (let i = 0; i < lotCapacity; i += 1) {
      const at = i * 16;
      this.lotMatrices[at] = 1;
      this.lotMatrices[at + 5] = 1;
      this.lotMatrices[at + 10] = 1;
      this.lotMatrices[at + 15] = 1;
      this.lotMatrices[at + 13] = 0.35;
    }

    this.ghostMaterial = new THREE.MeshBasicNodeMaterial({
      transparent: true,
      opacity: 0.32,
      depthWrite: false,
    });
    this.ghost = new THREE.Mesh(new THREE.BoxGeometry(1, 1, 1), this.ghostMaterial);
    this.ghost.visible = false;
    this.ghost.renderOrder = 2;
    this.scene.add(this.ghost);
  }

  async init(): Promise<void> {
    await this.renderer.init();
    this.resize();
  }

  resize(): void {
    const width = window.innerWidth;
    const height = window.innerHeight;
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(width, height, false);
    this.frame();
  }

  /**
   * Fly the camera to a plot cell and close in on it — what an analytics row
   * does when it is clicked. The camera walks there over the next few frames
   * rather than cutting, because a cut loses the player's place on the floor.
   */
  focus(cellX: number, cellY: number): void {
    this.viewTarget.x = cellX - this.plotW / 2 + 0.5;
    this.viewTarget.z = cellY - this.plotH / 2 + 0.5;
    this.viewTarget.zoom = FOCUS_ZOOM;
  }

  /**
   * Turn the LOD tiers on or off. The bench flips this to price them; there is
   * no reason for a player to.
   */
  setLod(enabled: boolean): void {
    this.lodEnabled = enabled;
  }

  /**
   * The tiers this frame would use, derived from where the camera is and how
   * close. Zooming in shrinks both radii, which is the whole point: a floor
   * being *inspected* has most of itself off screen.
   */
  private lod(): Lod {
    if (!this.lodEnabled) return LOD_OFF;
    const span = Math.max(this.plotW, this.plotH, 8);
    const half = span * 0.62 * this.view.zoom;
    const aspect = window.innerWidth / Math.max(window.innerHeight, 1);
    const cullRadius = Math.max(half, half * aspect) * CULL_MARGIN;
    return { enabled: true, detailRadius: cullRadius * DETAIL_SHARE, cullRadius };
  }

  /** Back to the whole plot. */
  resetView(): void {
    this.viewTarget.x = 0;
    this.viewTarget.z = 0;
    this.viewTarget.zoom = 1;
  }

  /** One frame of camera travel. Returns true while it is still moving. */
  private fly(): boolean {
    const dx = this.viewTarget.x - this.view.x;
    const dz = this.viewTarget.z - this.view.z;
    const dz2 = this.viewTarget.zoom - this.view.zoom;
    if (Math.abs(dx) < 0.01 && Math.abs(dz) < 0.01 && Math.abs(dz2) < 0.001) {
      return false;
    }
    this.view.x += dx * FLY_RATE;
    this.view.z += dz * FLY_RATE;
    this.view.zoom += dz2 * FLY_RATE;
    this.camera.position.set(this.view.x + 60, 60, this.view.z + 60);
    this.camera.lookAt(this.view.x, 0, this.view.z);
    this.frame();
    return true;
  }

  /** Frame the camera on the plot. Called on resize and when the plot changes. */
  private frame(): void {
    const span = Math.max(this.plotW, this.plotH, 8);
    const half = span * 0.62 * this.view.zoom;
    const aspect = window.innerWidth / Math.max(window.innerHeight, 1);
    this.camera.left = -half * aspect;
    this.camera.right = half * aspect;
    this.camera.top = half;
    this.camera.bottom = -half;
    this.camera.updateProjectionMatrix();
  }

  /**
   * Turn a click into a floor cell, so the UI can aim a command at a place.
   * Unproject rather than raycast: the ground is a plane at y = 0 and this is
   * three lines instead of an intersection test.
   */
  cellAt(clientX: number, clientY: number): { x: number; y: number } {
    const ndc = new THREE.Vector3(
      (clientX / window.innerWidth) * 2 - 1,
      -(clientY / window.innerHeight) * 2 + 1,
      -1,
    ).unproject(this.camera);
    const direction = new THREE.Vector3(0, 0, -1).applyQuaternion(this.camera.quaternion);
    const t = -ndc.y / direction.y;
    const hit = ndc.addScaledVector(direction, t);
    return {
      x: Math.floor(hit.x + this.plotW / 2),
      y: Math.floor(hit.z + this.plotH / 2),
    };
  }

  /**
   * Turn a click into the cell of the *thing* under the pointer (M1.2).
   *
   * The ground-plane unproject above is right for placement — you aim at
   * floor — but wrong for pointing at a machine: on an isometric camera a
   * click on a tall cuboid's body lands on the ground cell *behind* it, so
   * selecting a furnace meant clicking its base. This walks the structure
   * boxes the renderer already draws, intersects the pointer ray with each
   * (axis-aligned, so the slab test is a few lines), and answers the cell
   * under the nearest hit — clamped inside the box's footprint, so a hit on
   * a side face names a cell the structure actually covers. `null` when the
   * pointer is on bare floor; the caller falls back to [`cellAt`].
   */
  pickAt(clientX: number, clientY: number): { x: number; y: number } | null {
    const origin = new THREE.Vector3(
      (clientX / window.innerWidth) * 2 - 1,
      -(clientY / window.innerHeight) * 2 + 1,
      -1,
    ).unproject(this.camera);
    const direction = new THREE.Vector3(0, 0, -1).applyQuaternion(this.camera.quaternion);
    let nearest = Infinity;
    let found: { x: number; z: number; w: number; d: number } | null = null;
    for (const box of this.pickBoxes) {
      const t = rayBox(origin, direction, box);
      if (t != null && t < nearest) {
        nearest = t;
        found = box;
      }
    }
    if (found == null) return null;
    const eps = 1e-4;
    const hx = Math.min(
      Math.max(origin.x + direction.x * nearest, found.x - found.w / 2 + eps),
      found.x + found.w / 2 - eps,
    );
    const hz = Math.min(
      Math.max(origin.z + direction.z * nearest, found.z - found.d / 2 + eps),
      found.z + found.d / 2 - eps,
    );
    return {
      x: Math.floor(hx + this.plotW / 2),
      y: Math.floor(hz + this.plotH / 2),
    };
  }

  /** Plot size the renderer last saw, for hit-testing against the plot edge. */
  plot(): { w: number; h: number } {
    return { w: this.plotW, h: this.plotH };
  }

  /**
   * Draw one frame from the two most recent published states.
   *
   * `alpha` is clamped to `[0, 1]`: running past the newest snapshot would be
   * extrapolation, and extrapolated positions are the shape of bug that ends up
   * looking like a sim problem. Better to hold still for a frame.
   */
  render(
    previous: FrameSnapshot | null,
    latest: FrameSnapshot | null,
    alpha: number,
    cursor: CursorGhost | null,
  ): FrameCost {
    const startedAt = performance.now();
    let drawn = 0;
    let used = 0;
    const lod = this.lod();

    if (latest) {
      if (latest.plotW !== this.plotW || latest.plotH !== this.plotH) {
        this.rebuildGround(latest.plotW, latest.plotH);
      }
      if (latest.structureEpoch !== this.structureEpoch) {
        this.uploadStructures(latest);
      }

      const blend = previous && previous.seq !== latest.seq ? Math.min(Math.max(alpha, 0), 1) : 1;
      drawn = Math.min(latest.count, this.capacity);
      this.uploadCrowd(previous, latest, blend, drawn, lod);
      this.drawLots(previous, latest, blend);
      this.uploadHeat(latest);
      used = blend;
    }

    this.fly();
    this.placeGhost(cursor);

    const boundaryMs = performance.now() - startedAt;
    const renderStartedAt = performance.now();
    this.renderer.render(this.scene, this.camera);
    return {
      boundaryMs,
      renderMs: performance.now() - renderStartedAt,
      drawn,
      structures: this.structures.count,
      lots: this.lots.count,
      alpha: used,
      lod,
    };
  }

  /**
   * Hand the two newest published agent arrays to the GPU and dispatch the
   * compute pass. Two `set()` calls and one dispatch, whatever the crowd size:
   * this is the whole of the main thread's per-frame work for the people on the
   * floor, and the shape B0.6 will stress.
   *
   * When the crowd has *grown* since the previous publish the tail has no
   * "from" to interpolate out of, so the fresh array is written into both
   * buffers for those instances — a new body appears where it is rather than
   * sliding in from wherever the last one at that index stood.
   */
  private uploadCrowd(
    previous: FrameSnapshot | null,
    latest: FrameSnapshot,
    blend: number,
    drawn: number,
    lod: Lod,
  ): void {
    const crowd = this.crowd;
    const to = latest.instances;
    const from = previous?.instances;
    const shared = from ? Math.min(drawn, previous.count) : 0;
    const target = crowd.to.value.array as Float32Array;
    const source = crowd.from.value.array as Float32Array;
    target.set(to.subarray(0, drawn * 4));
    if (from && shared > 0) {
      source.set(from.subarray(0, shared * 4));
    }
    if (shared < drawn) {
      source.set(to.subarray(shared * 4, drawn * 4), shared * 4);
    }
    crowd.to.value.needsUpdate = true;
    crowd.from.value.needsUpdate = true;
    crowd.blend.value = blend;
    crowd.live.value = drawn;
    crowd.clock.value = performance.now() / 1000;
    // Four scalars for the LOD tiers, whatever the crowd size. The whole point
    // of doing detail selection in the compute pass is that this stays four.
    crowd.viewX.value = this.view.x;
    crowd.viewZ.value = this.view.z;
    crowd.detailRadius.value = lod.detailRadius;
    crowd.cullRadius.value = lod.cullRadius;
    crowd.mesh.count = drawn;
    this.renderer.compute(crowd.step);
  }

  /**
   * Lots, interpolated between the two newest publishes exactly as agents are —
   * but on the CPU, because there are at most a WIP cap's worth of them and a
   * second compute pipeline to maintain is not worth sixty-four boxes.
   *
   * Matching lots by *array index* across two snapshots is only safe because
   * the sim keeps them in id order and only ever appends or removes: a lot that
   * finished shifts everything behind it by one, which for one frame blends two
   * neighbours and is invisible at a walking pace. Sorting or shuffling them
   * would make this wrong, which is why the sim does not.
   */
  private drawLots(
    previous: FrameSnapshot | null,
    latest: FrameSnapshot,
    blend: number,
  ): void {
    const to = latest.lots;
    const from = previous?.lots;
    const shared = from ? Math.min(latest.lotCount, previous.lotCount) : 0;
    const drawn = Math.min(latest.lotCount, this.lotCapacity);

    for (let i = 0; i < drawn; i += 1) {
      const src = i * 4;
      const at = i * 16;
      if (i < shared && from) {
        this.lotMatrices[at + 12] = from[src]! + (to[src]! - from[src]!) * blend;
        this.lotMatrices[at + 14] = from[src + 1]! + (to[src + 1]! - from[src + 1]!) * blend;
      } else {
        this.lotMatrices[at + 12] = to[src]!;
        this.lotMatrices[at + 14] = to[src + 1]!;
      }
      // State code, plus a half-step when the lot is expedited.
      const code = to[src + 2]!;
      const hot = code - Math.floor(code) > 0.25;
      const state = Math.floor(code) % LOT_COLORS.length;
      this.scratch.setHex(hot ? HOT_COLOR : LOT_COLORS[state]!);
      // Progress along the route brightens the crate, so a lot near the end of
      // its thirty steps looks different from one that just came in the door.
      this.scratch.multiplyScalar(0.6 + 0.4 * to[src + 3]!);
      const rgb = i * 3;
      this.lotColors[rgb] = this.scratch.r;
      this.lotColors[rgb + 1] = this.scratch.g;
      this.lotColors[rgb + 2] = this.scratch.b;
    }

    this.lots.count = drawn;
    this.lots.instanceMatrix.needsUpdate = true;
    if (this.lots.instanceColor) this.lots.instanceColor.needsUpdate = true;
  }

  /**
   * The WIP glow. One byte per plot cell straight into a `DataTexture` — the
   * cheapest way to put a field on the floor, and the reason the sim publishes
   * a plane rather than a list: no scatter step on this side, one upload.
   *
   * There is no epoch to skip on. WIP moves every tick, which is exactly what
   * makes this the section B0.6 should be measuring.
   */
  private uploadHeat(snapshot: FrameSnapshot): void {
    if (snapshot.heatW < 1 || snapshot.heatH < 1) return;
    if (
      !this.heatTexture ||
      this.heatTexture.image.width !== snapshot.heatW ||
      this.heatTexture.image.height !== snapshot.heatH
    ) {
      this.rebuildHeat(snapshot.heatW, snapshot.heatH);
    }
    // The field is the sim's to state, so the tint follows the slot rather than
    // whatever the overlay last asked for.
    if (snapshot.heatView !== this.heatView && this.heatMaterial) {
      this.heatView = snapshot.heatView;
      this.heatMaterial.colorNode = tintOf(snapshot.heatView);
      this.heatMaterial.needsUpdate = true;
    }
    const target = this.heatTexture;
    if (!target) return;
    (target.image.data as Uint8Array).set(snapshot.heat);
    target.needsUpdate = true;
  }

  /** Size the heat texture and its plane to the plot the sim published. */
  private rebuildHeat(w: number, h: number): void {
    if (this.heatPlane) {
      this.scene.remove(this.heatPlane);
      this.heatPlane.geometry.dispose();
    }
    this.heatTexture?.dispose();

    const data = new Uint8Array(w * h);
    const heatMap = new THREE.DataTexture(data, w, h, THREE.RedFormat, THREE.UnsignedByteType);
    heatMap.minFilter = THREE.LinearFilter;
    heatMap.magFilter = THREE.LinearFilter;
    heatMap.needsUpdate = true;
    this.heatTexture = heatMap;

    const material = new THREE.MeshBasicNodeMaterial({
      transparent: true,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
    material.colorNode = tintOf(this.heatView);
    // **The glow's ramp** (B0.7). Linear opacity spent most of its range on the
    // difference between "very busy" and "very busy", and almost none on the
    // difference a diagnosis actually turns on — *is anything piling up here at
    // all*. A single lot at the shipped heat-per-lot is 11% of the byte range
    // and was very nearly invisible against the plate.
    //
    // The curve below lifts the bottom of the range without flattening the top:
    // one lot reads at about a quarter opacity, four at a half, and a saturated
    // cell still separates from a busy one. Presentation arithmetic on a value
    // the sim derived, which is exactly where a curve like this belongs — the
    // *number* stays linear in lots, and only the ink is shaped.
    material.opacityNode = textureNode(heatMap, uv()).r.pow(HEAT_GAMMA).mul(0.9);
    this.heatMaterial = material;

    // Rotated **+**90°, not −90°: that maps the plane's `v = 0` edge to world
    // −z, which is where the sim's cell row 0 lives. Getting this backwards
    // would mirror the glow against the layout it describes — and the plane
    // then faces down, hence `DoubleSide`. Done by geometry rather than by
    // `flipY`, which data textures do not all honour.
    const plane = new THREE.Mesh(new THREE.PlaneGeometry(w, h), material);
    plane.rotation.x = Math.PI / 2;
    plane.position.y = 0.03;
    plane.renderOrder = 1;
    plane.frustumCulled = false;
    this.heatPlane = plane;
    this.scene.add(plane);
  }

  /**
   * Read the site out of the snapshot into the instance buffer. Runs on the
   * frames where someone built something, and on no others.
   */
  private uploadStructures(snapshot: FrameSnapshot): void {
    const source = snapshot.structures;
    const count = Math.min(snapshot.structureCount, this.structureCapacity);
    this.pickBoxes.length = 0;
    for (let i = 0; i < count; i += 1) {
      const src = i * 8;
      const at = i * 16;
      const w = source[src + 2]!;
      const d = source[src + 3]!;
      const height = source[src + 4]!;
      const isTool = source[src + 5]! >= CLASS_TOOL;
      const type = source[src + 6]! | 0;
      const flags = source[src + 7]! | 0;

      // Scale on the diagonal, translation in the last column: a unit box
      // becomes a room plate or a tool crate without a matrix multiply.
      this.structureMatrices.fill(0, at, at + 16);
      this.structureMatrices[at] = w;
      this.structureMatrices[at + 5] = Math.max(height, 0.02);
      this.structureMatrices[at + 10] = d;
      this.structureMatrices[at + 12] = source[src]!;
      this.structureMatrices[at + 13] = height / 2;
      this.structureMatrices[at + 14] = source[src + 1]!;
      this.structureMatrices[at + 15] = 1;

      // The same box, kept for picking (`pickAt`): tools as their full
      // height, rooms as the plates they are.
      this.pickBoxes.push({ x: source[src]!, z: source[src + 1]!, w, d, h: Math.max(height, 0.02) });

      const palette = isTool ? this.toolColors : this.roomColors;
      const colour = palette[type] ?? palette[0];
      this.scratch.copy(colour ?? new THREE.Color(0x808080));
      // The inspected thing is the one that glows; everything else is flat.
      if ((flags & FLAG_SELECTED) !== 0) this.scratch.lerp(new THREE.Color(0xffffff), 0.45);
      const rgb = i * 3;
      this.structureColors[rgb] = this.scratch.r;
      this.structureColors[rgb + 1] = this.scratch.g;
      this.structureColors[rgb + 2] = this.scratch.b;
    }
    this.structures.count = count;
    this.structures.instanceMatrix.needsUpdate = true;
    if (this.structures.instanceColor) this.structures.instanceColor.needsUpdate = true;
    this.structureEpoch = snapshot.structureEpoch;
  }

  private placeGhost(cursor: CursorGhost | null): void {
    if (!cursor || cursor.w < 1 || cursor.h < 1) {
      this.ghost.visible = false;
      return;
    }
    const height = Math.max(cursor.height, 0.4);
    this.ghost.visible = true;
    this.ghost.scale.set(cursor.w, height, cursor.h);
    this.ghost.position.set(
      cursor.x + cursor.w / 2 - this.plotW / 2,
      height / 2 + 0.02,
      cursor.y + cursor.h / 2 - this.plotH / 2,
    );
    this.ghostMaterial.color.setHex(GHOST_COLORS[cursor.verdict]);
  }

  /** The plot itself: a plate and a grid, sized to the site the sim published. */
  private rebuildGround(w: number, h: number): void {
    if (this.ground) {
      this.scene.remove(this.ground);
      this.ground.traverse((node) => {
        const mesh = node as THREE.Mesh;
        mesh.geometry?.dispose();
      });
    }
    this.plotW = w;
    this.plotH = h;
    const group = new THREE.Group();
    const plate = new THREE.Mesh(
      new THREE.BoxGeometry(w, 0.4, h),
      new THREE.MeshStandardNodeMaterial({ color: 0x1b2026, roughness: 0.95 }),
    );
    plate.position.y = -0.24;
    group.add(plate);
    const grid = new THREE.GridHelper(Math.max(w, h), Math.max(w, h) / 4, 0x2f3a44, 0x232b33);
    grid.position.y = -0.02;
    group.add(grid);
    this.ground = group;
    this.scene.add(group);
    this.frame();
  }

  dispose(): void {
    this.renderer.dispose();
  }
}

/** The floor plane's tint for a published field code. */
function tintOf(view: number) {
  const hex = HEAT_TINTS[view] ?? HEAT_TINTS[0]!;
  const color = new THREE.Color(hex);
  return vec3(color.r, color.g, color.b);
}

/**
 * Build the crowd: two storage buffers for the snapshots either side of the
 * blend, three for what the compute pass produces, one dispatch, one draw.
 *
 * A free function rather than constructor code so its return type can be
 * *inferred* — the node types three exposes here are internal, and naming them
 * in a class field would mean hand-writing a type the library already knows.
 */
function buildCrowd(capacity: number, catalog: SiteCatalogUi) {
  // The two most recent published agent arrays, uploaded verbatim.
  const from = instancedArray(capacity, 'vec4');
  const to = instancedArray(capacity, 'vec4');
  // What the pass writes: where each body is, what colour, and how it stands.
  const offset = instancedArray(capacity, 'vec3');
  const tint = instancedArray(capacity, 'vec3');
  const pose = instancedArray(capacity, 'vec3');

  // The palette, indexed by the published code itself — `role × 8 + doing`, so
  // one lookup carries both who somebody is and what they are up to. Built on
  // the CPU at boot from the catalog's authored role colours and the state
  // accents above, which is where the blending is convenient and where doing it
  // once rather than per instance per frame belongs.
  const roles = Math.max(catalog.crew.length, 1);
  const palette = instancedArray(new Float32Array(roles * DOING_SLOTS * 4), 'vec4');
  const paletteData = palette.value.array as Float32Array;
  const swatch = new THREE.Color();
  const accent = new THREE.Color();
  for (let role = 0; role < roles; role += 1) {
    const authored = catalog.crew[role]?.color ?? '#c0c0c0';
    for (let doing = 0; doing < DOING_SLOTS; doing += 1) {
      const state = DOING_ACCENTS[doing] ?? DOING_ACCENTS[0]!;
      swatch.set(authored);
      accent.setHex(state.accent);
      swatch.lerp(accent, state.weight);
      paletteData.set([swatch.r, swatch.g, swatch.b, 1], (role * DOING_SLOTS + doing) * 4);
    }
  }
  palette.value.needsUpdate = true;

  const blend = uniform(1);
  const live = uniform(0);
  const clock = uniform(0);
  // The LOD tiers (B0.6): where the camera is looking, and the two radii around
  // it. Four scalars a frame, for a crowd of any size.
  const viewX = uniform(0);
  const viewZ = uniform(0);
  const detailRadius = uniform(1e6);
  const cullRadius = uniform(1e6);

  const step = Fn(() => {
    const a = from.element(instanceIndex);
    const b = to.element(instanceIndex);
    // `[x, z, code, fatigue]`, the code carrying role × 8 + doing.
    const here = mix(a.xy, b.xy, blend);
    const code = b.z.floor();
    const doing = code.mod(float(DOING_SLOTS));
    const fatigue = b.w;
    // Anything past the live count is scaled to nothing rather than branched
    // around: one dispatch, no divergence, and nothing on screen.
    const shown = float(instanceIndex).lessThan(live).select(float(1), float(0));

    // The LOD tiers, as multipliers rather than as branches — for the same
    // reason `shown` is one. Squared distances, so no square root runs for
    // every body every frame to answer a question about a threshold.
    const dx = here.x.sub(viewX);
    const dz = here.y.sub(viewZ);
    const away = dx.mul(dx).add(dz.mul(dz));
    const detailed = away.lessThan(detailRadius.mul(detailRadius)).select(float(1), float(0));
    const visible = away.lessThan(cullRadius.mul(cullRadius)).select(float(1), float(0));

    // A walking body bobs and a standing one does not; the phase is offset by
    // index so a crowd is not a chorus line. Past the detail radius neither the
    // bob nor the stoop is legible, so neither is applied.
    const walking = doing.greaterThan(float(0.5)).and(doing.lessThan(float(3.5)));
    const bob = sin(clock.mul(6).add(float(instanceIndex).mul(1.7)))
      .mul(0.06)
      .mul(walking.select(float(1), float(0)))
      .mul(detailed);
    // And a tired one stoops — which is why fatigue rides in the instance quad
    // rather than only in the overlay's average.
    const height = float(1)
      .sub(fatigue.mul(0.18).mul(detailed))
      .mul(shown)
      .mul(visible);

    offset.element(instanceIndex).assign(vec3(here.x, bob, here.y));
    pose.element(instanceIndex).assign(vec3(visible, height, visible));
    tint.element(instanceIndex).assign(palette.element(code.toInt()).xyz.mul(shown));
  })().compute(capacity);

  const material = new THREE.MeshStandardNodeMaterial({ roughness: 0.6, metalness: 0.05 });
  material.positionNode = positionLocal.mul(pose.toAttribute()).add(offset.toAttribute());
  material.colorNode = tint.toAttribute();

  // The box is modelled standing *on* the floor rather than centred on it, so
  // the pose can squash it from the feet up.
  const mesh = new THREE.InstancedMesh(
    new THREE.BoxGeometry(0.7, 1.7, 0.7).translate(0, 0.85, 0),
    material,
    capacity,
  );
  mesh.frustumCulled = false;
  mesh.count = 0;
  // `InstancedMesh` allocates its transform array **zeroed**, not as identities,
  // and the node material still multiplies by it — so a crowd whose placement
  // comes entirely from the compute pass would collapse to a point at the
  // origin without this. Written once at construction and never again: the
  // per-frame transform is the compute pass's `offset` and `pose`.
  const identity = mesh.instanceMatrix.array as Float32Array;
  for (let i = 0; i < capacity; i += 1) {
    const at = i * 16;
    identity[at] = 1;
    identity[at + 5] = 1;
    identity[at + 10] = 1;
    identity[at + 15] = 1;
  }
  mesh.instanceMatrix.needsUpdate = true;

  return {
    mesh,
    from,
    to,
    blend,
    live,
    clock,
    viewX,
    viewZ,
    detailRadius,
    cullRadius,
    step,
  };
}

/**
 * Ray vs. axis-aligned box (the slab test), for `pickAt`. The box stands on
 * the ground: x/z centred, y from 0 to `h`. Answers the entry distance along
 * the ray, or `null` for a miss.
 */
function rayBox(
  origin: THREE.Vector3,
  direction: THREE.Vector3,
  box: { x: number; z: number; w: number; d: number; h: number },
): number | null {
  let tNear = -Infinity;
  let tFar = Infinity;
  const axes: [number, number, number][] = [
    [origin.x, direction.x === 0 ? 1e-12 : direction.x, 0],
    [origin.y, direction.y === 0 ? 1e-12 : direction.y, 1],
    [origin.z, direction.z === 0 ? 1e-12 : direction.z, 2],
  ];
  for (const [at, along, axis] of axes) {
    const low = axis === 0 ? box.x - box.w / 2 : axis === 1 ? 0 : box.z - box.d / 2;
    const high = axis === 0 ? box.x + box.w / 2 : axis === 1 ? box.h : box.z + box.d / 2;
    const t1 = (low - at) / along;
    const t2 = (high - at) / along;
    tNear = Math.max(tNear, Math.min(t1, t2));
    tFar = Math.min(tFar, Math.max(t1, t2));
    if (tNear > tFar) return null;
  }
  return tFar < 0 ? null : Math.max(tNear, 0);
}

